import type { CcCase, Side } from '../../../services/carrierCorrespondenceService';

/** How a case's state reads on screen, and what it should look like. */
export const STATUS: Record<string, { tone: 'good' | 'bad' | 'warning' | 'neutral'; label: string; pulse?: boolean }> = {
  waiting: { tone: 'warning', label: 'Stalled' },
  calling: { tone: 'good', label: 'Calling', pulse: true },
  call_ended: { tone: 'good', label: 'Reading the call', pulse: true },
  answered: { tone: 'good', label: 'Answered' },
  needs_person: { tone: 'bad', label: 'Needs a person' },
};

/**
 * Whose move it is. The most useful thing a call produces, so it gets colour:
 * a case waiting on our own team is one we can clear this afternoon, which is a
 * different feeling from one sitting with an underwriter.
 */
export const SIDE: Record<Side, { tone: 'good' | 'bad' | 'warning' | 'neutral'; label: string }> = {
  carrier: { tone: 'neutral', label: 'Waiting on Carrier' },
  client: { tone: 'warning', label: 'Waiting on Customer' },
  agency: { tone: 'bad', label: 'Waiting on our own Team' },
};

/** "Medical report", whatever case the carrier said it in. */
export const capital = (text: string | null | undefined): string =>
  (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '');

/**
 * When the carrier said a case would move, said so it cannot be mistaken for
 * any other date: "Expected to move by 5 Oct 2026", never a bare "2026-10-05".
 */
export const expectedText = (carrier: string, expected: string | null | undefined): string | null => {
  if (!expected) return null;
  const date = /^\d{4}-\d{2}-\d{2}/.test(expected) ? new Date(`${expected.slice(0, 10)}T12:00:00Z`) : null;
  if (date && !Number.isNaN(date.getTime())) {
    return `Expected to move by ${date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  const said = expected.replace(/\.$/, '');
  return `${carrier} said ${said.charAt(0).toLowerCase()}${said.slice(1)}`;
};

/** How a call that is still going reads, where its button would otherwise be. */
export const CALLING: Record<'calling' | 'call_ended', string> = {
  calling: 'On the call',
  call_ended: 'Reading the call',
};

export const money = (value: number | null | undefined): string =>
  value == null ? '—' : `$${Math.round(value).toLocaleString()}`;

/** "6 days", "3 weeks" — how long it has sat, the way a person would say it. */
export const stalledFor = (days: number): string => {
  if (days <= 1) return 'a day';
  if (days < 14) return `${days} days`;
  const weeks = Math.floor(days / 7);
  return weeks < 9 ? `${weeks} weeks` : `${Math.floor(days / 30)} months`;
};

/** Long-stalled cases should look worse the longer they sit. */
export const ageTone = (days: number): 'bad' | 'warning' | 'neutral' =>
  days >= 30 ? 'bad' : days >= 14 ? 'warning' : 'neutral';

/**
 * What to say for a case the carrier has not answered on. A case chased three
 * times with nobody picking up is not "not called", and saying so would hide
 * the work the agent has already done.
 */
export const notAnswered = (row: CcCase, { short = false } = {}): string => {
  if (!row.attempts) return 'Not called yet';
  if (short) return 'No answer yet';
  return `Called ${row.attempts} time${row.attempts === 1 ? '' : 's'}, nobody at ${row.carrier} has picked up yet`;
};

/** Everything a search box should match on a case. */
export const searchable = (row: CcCase): (string | number | null)[] => [
  row.policy_num, row.carrier, row.applicant, row.stage,
  row.reason, row.blocking_item, row.rep_name, row.reference_number, row.state,
];
